/**
 * ColorPopover — a self-contained "Set color" popover matching the Figma design:
 * a None option, a preset swatch grid, a saturation/value square + hue bar, and
 * a Hex input. No external dependency. `onChange(hex)` fires as the user picks;
 * "None" resets the current run to the field's base color.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Color conversions (HSV ↔ hex) ──────────────────────────────────────────

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Preset swatch grid: 10 hue columns × 5 tints, plus a grayscale row.
const HUES = [0, 28, 50, 90, 140, 175, 200, 222, 265, 320];
const TINTS = [
  { s: 0.32, v: 1 },
  { s: 0.55, v: 0.92 },
  { s: 0.78, v: 0.8 },
  { s: 0.9, v: 0.6 },
  { s: 0.95, v: 0.42 },
];
const GRAYS = ['#ffffff', '#d9d9d9', '#b3b3b3', '#808080', '#4d4d4d', '#1a1a1a', '#000000'];

// ── Component ───────────────────────────────────────────────────────────────

export function ColorPopover({
  value,
  onChange,
  baseColor = '#0f141a',
  disabled = false,
}: {
  value: string;
  onChange: (hex: string) => void;
  /** Applied when the user picks "None" (the field's inherited text color). */
  baseColor?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('settings');
  const [open, setOpen] = useState(false);
  const [hsv, setHsv] = useState(() => hexToHsv(value || baseColor));
  const [hexText, setHexText] = useState(value || baseColor);
  const rootRef = useRef<HTMLDivElement>(null);

  // Sync internal state from the incoming value whenever the popover opens.
  useEffect(() => {
    if (open) {
      setHsv(hexToHsv(value || baseColor));
      setHexText(value || baseColor);
    }
  }, [open, value, baseColor]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const emit = (h: number, s: number, v: number) => {
    const hex = hsvToHex(h, s, v);
    setHsv({ h, s, v });
    setHexText(hex);
    onChange(hex);
  };

  // Pointer-drag helper for the SV square and hue bar.
  const dragFrom = (el: HTMLElement, onMove: (x: number, y: number) => void) => (
    e: React.PointerEvent,
  ) => {
    const rect = el.getBoundingClientRect();
    const handle = (clientX: number, clientY: number) => {
      const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
      onMove(x, y);
    };
    handle(e.clientX, e.clientY);
    const move = (ev: PointerEvent) => handle(ev.clientX, ev.clientY);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  return (
    <div className="rbms-ewh-colorpop" ref={rootRef}>
      <button
        type="button"
        className="rbms-ewh-color"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-label={t('homepageEditor.textColor')}
      >
        <span className="rbms-ewh-color__label" style={{ color: value || undefined }}>{t('homepageEditor.color')}</span>
        <span className="rbms-ewh-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="rbms-ewh-setcolor" role="dialog" aria-label={t('homepageEditor.setColor')}>
          <div className="rbms-ewh-setcolor__title">{t('homepageEditor.setColor')}</div>

          <button
            type="button"
            className="rbms-ewh-setcolor__none"
            onClick={() => {
              onChange(baseColor);
              setOpen(false);
            }}
          >
            {t('homepageEditor.colorNone')}
          </button>

          <div className="rbms-ewh-setcolor__swatches">
            {TINTS.map((t, r) =>
              HUES.map((h, c) => {
                const hex = hsvToHex(h, t.s, t.v);
                return (
                  <button
                    key={`${r}-${c}`}
                    type="button"
                    className="rbms-ewh-setcolor__cell"
                    style={{ background: hex }}
                    onClick={() => emit(h, t.s, t.v)}
                    aria-label={hex}
                  />
                );
              }),
            )}
            {GRAYS.map((hex) => (
              <button
                key={hex}
                type="button"
                className="rbms-ewh-setcolor__cell"
                style={{ background: hex }}
                onClick={() => {
                  const { h, s, v } = hexToHsv(hex);
                  emit(h, s, v);
                }}
                aria-label={hex}
              />
            ))}
          </div>

          {/* Saturation (x) / Value (y) square */}
          <div
            className="rbms-ewh-setcolor__sv"
            style={{
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))`,
            }}
            onPointerDown={(e) =>
              dragFrom(e.currentTarget, (x, y) => emit(hsv.h, x, 1 - y))(e)
            }
          >
            <span
              className="rbms-ewh-setcolor__sv-handle"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>

          {/* Hue bar */}
          <div
            className="rbms-ewh-setcolor__hue"
            onPointerDown={(e) => dragFrom(e.currentTarget, (x) => emit(x * 360, hsv.s, hsv.v))(e)}
          >
            <span className="rbms-ewh-setcolor__hue-handle" style={{ left: `${(hsv.h / 360) * 100}%` }} />
          </div>

          <div className="rbms-ewh-setcolor__hexrow">
            <span className="rbms-ewh-setcolor__hexlabel">{t('homepageEditor.hex')}</span>
            <input
              className="rbms-ewh-setcolor__hexinput"
              value={hexText.replace('#', '')}
              onChange={(e) => {
                const next = `#${e.target.value.replace('#', '')}`;
                setHexText(next);
                if (HEX_RE.test(next)) {
                  const { h, s, v } = hexToHsv(next);
                  setHsv({ h, s, v });
                  onChange(next.toLowerCase());
                }
              }}
              aria-label={t('homepageEditor.hexValue')}
            />
          </div>
        </div>
      )}
    </div>
  );
}
